/**
 * <wallet-panel> — the one image a wallet pass carries, drawn in the browser.
 * Apple's bundled `strip*.png` and the hero Google fetches are one treatment
 * at two sizes, so the two schematics MOUNT this element rather than each
 * composing a band of its own: a second band is a second design the moment the
 * shared decision moves, and the preview an operator approves from is the
 * surface that would lie.
 *
 * EVERY TONE AND EVERY EDGE COMES FROM `walletPanelTreatment`
 * (`@momo/ticket-template/wallet`) — the same call the pass signer and the
 * hero painter make, over the same live brand seed — so a variant whose
 * treatment changes there changes here with nothing to edit. No colour, no
 * fraction, and no per-variant branch is authored in this file.
 *
 * A RAMP FALLS DOWN THE PANEL AND A PLATE IS ONE SOLID TONE, which is the
 * painter's own split: it interpolates a ramp per sRGB channel down the height
 * and fills a plate with a single tone edge to edge. So the ramp runs
 * `to bottom` with hex stops that interpolate in sRGB the way the painter's
 * channel arithmetic does, and the plate is that one colour with no gradient
 * anywhere in it.
 *
 * A COMPOSITION IS NOT PAINTED AT ALL — it is the band `renderWalletPanel`
 * (`@momo/ticket-template/wallet-panel`) composes from the treatment's own
 * plate/mark pair and the event's own name and mark, which is the call the
 * render service makes for the signed artwork. Mounting that call rather than
 * a stand-in is what lets the operator approve the wordmark the buyer's pass
 * carries; a plate drawn alone here would preview a bare slab against a pass
 * that ships letterforms.
 *
 * THE VENDOR'S OWN GLYPHS ARE THE SLOT, AND A COMPOSITION HAS NONE. Both
 * vendors draw over this panel and own every glyph they draw, so slotted
 * content is a stand-in for what the vendor will draw there — and a
 * band-carrying pass has no such glyph on the panel at all: the Apple composer
 * (firebase/src/settings/appleWallet.api.ts) moves the event name
 * off `primaryFields` the moment a band is supplied, precisely so a strip
 * spelling the name and a field repeating it never collide. So a ramp and a
 * flat carry the slot; a composition carries the band alone.
 *
 * THE ALLOTMENT IS THE CONSUMER'S, in the shared module's own numbers —
 * Apple's 375×98 strip against Google's 1032×336 hero. It fixes the band's
 * aspect AND the geometry the composition is composed at, so the two can never
 * disagree, and the band is then scaled whole into whatever box the consumer's
 * layout gives it — the package's own scaling-frame discipline: a scaled
 * rendering of the real layout, never a re-flow at the preview's smaller size,
 * which is what keeps the wordmark's measured fit the one the signed band
 * converges on.
 */
import { fromResizeObserver } from "@mhmo91/schmancy/directives";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import {
	WALLET_STRIP_HEIGHT,
	WALLET_STRIP_WIDTH,
	walletPanelTreatment,
} from "@momo/ticket-template/wallet";
import { renderWalletPanel } from "@momo/ticket-template/wallet-panel";
import {
	DEFAULT_TICKET_TEMPLATE_VARIANT,
	type TicketTemplateVariant,
} from "@momo/types";
import { css, html, type TemplateResult } from "lit";
import { property } from "lit/decorators.js";
import { takeUntil, tap } from "rxjs";
import { brandSeedColor } from "../workspace/workspace.state.ts";

@schmancyElement("wallet-panel")
export class WalletPanel extends SchmancyElement {
	@property({ attribute: false })
	variant: TicketTemplateVariant = DEFAULT_TICKET_TEMPLATE_VARIANT;

	/** The event this band is composed for — the same two facts the signer's
	 *  own payload carries, and the only ones a composition draws. */
	@property({ attribute: false })
	eventName = "";

	@property({ attribute: false })
	logoUrl: string | null = null;

	/** The vendor's allotment. Apple's strip is the standing value because it
	 *  is the surface the signed archive bundles; Google's hero is fetched, and
	 *  that consumer states its own. */
	@property({ attribute: false })
	widthPx: number = WALLET_STRIP_WIDTH;

	@property({ attribute: false })
	heightPx: number = WALLET_STRIP_HEIGHT;

	// Block, not the inline default: the panel is a band in the pass's own
	// column and takes the width its consumer gives it, which an inline host
	// would leave to the surrounding text flow instead.
	static styles = [
		css`
			:host {
				display: block;
			}
		`,
	];

	override connectedCallback(): void {
		super.connectedCallback();
		// A box that changes with no render of this element's own — the pass
		// frame narrowing with the viewport — still has to move the band's
		// scale, and only the observer sees that.
		fromResizeObserver(this)
			.pipe(
				tap(() => this.#scaleToBox()),
				takeUntil(this.disconnecting),
			)
			.subscribe();
	}

	override updated(changed: Map<PropertyKey, unknown>): void {
		super.updated(changed);
		this.#scaleToBox();
	}

	/** The one number mapping the vendor's allotment onto the box this panel
	 *  was given. Written to the host rather than through a re-render, so the
	 *  band that is already mounted keeps its own node — and its wordmark keeps
	 *  the fit it measured — across every resize. */
	#scaleToBox(): void {
		this.style.setProperty(
			"--wallet-panel-scale",
			String(this.clientWidth / this.widthPx),
		);
	}

	override render(): TemplateResult {
		const treatment = walletPanelTreatment(brandSeedColor.get(), this.variant);
		// One template for every kind, so a variant change repaints this band in
		// place rather than tearing it down and rebuilding it.
		let paint: string;
		switch (treatment.kind) {
			case "ramp":
				paint = `background:linear-gradient(to bottom, ${treatment.top}, ${treatment.bottom});`;
				break;
			case "flat":
				paint = `background:${treatment.tone};`;
				break;
			case "composition":
				paint = `background:${treatment.plate};color:${treatment.mark};`;
				break;
			default: {
				// A kind added to WalletPanelTreatment fails to compile here rather
				// than falling through to a band this panel paints wrong.
				const unhandled: never = treatment;
				throw new Error(
					`unhandled wallet panel treatment: ${String(unhandled)}`,
				);
			}
		}

		return html`
			<div
				class="relative grid w-full content-center overflow-hidden"
				style="aspect-ratio:${this.widthPx}/${this.heightPx};${paint}"
			>
				${
					treatment.kind === "composition"
						? html`
							<!-- Out of the grid's own flow and hung on the band's top-left
							     corner, because the band it holds is laid out at the
							     vendor's full allotment and only the scale below brings it
							     down to this box: centred as a grid item it would be
							     centred at that full height, and the corner the transform
							     grows from would sit above the band entirely. -->
							<div
								class="absolute top-0 left-0"
								style="transform:scale(var(--wallet-panel-scale, 1));transform-origin:top left;"
							>
								${renderWalletPanel({
									eventName: this.eventName,
									logoUrl: this.logoUrl,
									plate: treatment.plate,
									mark: treatment.mark,
									widthPx: this.widthPx,
									heightPx: this.heightPx,
								})}
							</div>
						`
						: html`<slot></slot>`
				}
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"wallet-panel": WalletPanel;
	}
}
